import { useCallback, useEffect, useRef, useState } from 'react';
import { doc, getDoc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import type { OnboardingTemplateItem } from '../types';
import {
  clearLegacySimulateUnderContractLocalStorage,
  parseSimulatedSchedules,
  parseSimulatedUnderContractProjectIds,
  STAFF_PLANNING_COLLECTION,
  STAFF_PLANNING_HUB_DOC_ID,
  type SimulatedScheduleItem,
  type SimulatedSchedulesByProject,
} from '../utils/simulateUnderContract';

/**
 * Shared staff "Simulate under contract" overlay — per selected project.
 * Reads/writes `staffPlanning/hub` (employee/admin only via rules) — never
 * timberProjects.phase / contractedAt / proposals / money / real onboarding.
 */
export function useSimulateUnderContract(opts: {
  allowed: boolean;
  userId?: string | null;
  userName?: string | null;
}) {
  const { allowed, userId, userName } = opts;
  const [simulatedProjectIds, setSimulatedProjectIds] = useState<string[]>([]);
  const [simulatedSchedules, setSimulatedSchedules] = useState<SimulatedSchedulesByProject>({});
  const [hydrated, setHydrated] = useState(false);
  const [saving, setSaving] = useState(false);
  const [persistError, setPersistError] = useState<string | null>(null);
  const [templateItems, setTemplateItems] = useState<OnboardingTemplateItem[] | null>(null);
  const [templateLoadError, setTemplateLoadError] = useState<string | null>(null);

  const idsRef = useRef(simulatedProjectIds);
  idsRef.current = simulatedProjectIds;
  const schedulesRef = useRef(simulatedSchedules);
  schedulesRef.current = simulatedSchedules;
  const hasSimulations = simulatedProjectIds.length > 0;

  useEffect(() => {
    clearLegacySimulateUnderContractLocalStorage();
  }, []);

  useEffect(() => {
    if (!allowed) {
      setSimulatedProjectIds([]);
      setSimulatedSchedules({});
      setHydrated(true);
      setPersistError(null);
      return undefined;
    }
    setHydrated(false);
    const ref = doc(db, STAFF_PLANNING_COLLECTION, STAFF_PLANNING_HUB_DOC_ID);
    return onSnapshot(
      ref,
      (snap) => {
        const data = snap.data();
        setSimulatedProjectIds(parseSimulatedUnderContractProjectIds(data));
        setSimulatedSchedules(parseSimulatedSchedules(data));
        setHydrated(true);
        setPersistError(null);
      },
      (err) => {
        console.warn('staffPlanning/hub snapshot:', err);
        setSimulatedProjectIds([]);
        setSimulatedSchedules({});
        setHydrated(true);
        setPersistError(err instanceof Error ? err.message : 'Could not load shared planning preference');
      },
    );
  }, [allowed]);

  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'settings', 'onboardingChecklist'));
        if (cancelled) return;
        if (!snap.exists()) {
          setTemplateItems([]);
          setTemplateLoadError('No onboarding template configured');
          return;
        }
        const data = snap.data();
        const items = Array.isArray(data?.items) ? (data.items as OnboardingTemplateItem[]) : [];
        setTemplateItems(items);
        setTemplateLoadError(items.length ? null : 'Onboarding template has no items');
      } catch (err) {
        if (cancelled) return;
        console.warn('simulateUnderContract: failed to load onboarding template', err);
        setTemplateItems([]);
        setTemplateLoadError('Failed to load onboarding template');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [allowed]);

  const persistIds = useCallback(
    async (nextIds: string[]) => {
      if (!allowed) return;
      if (!userId) {
        setPersistError('Sign in required to save planning selections');
        return;
      }
      const prev = idsRef.current;
      setSaving(true);
      setPersistError(null);
      setSimulatedProjectIds(nextIds);
      idsRef.current = nextIds;
      try {
        await setDoc(
          doc(db, STAFF_PLANNING_COLLECTION, STAFF_PLANNING_HUB_DOC_ID),
          {
            simulatedUnderContractProjectIds: nextIds,
            // Clear the old all-projects boolean so leftover true does not come back.
            simulateUnderContract: false,
            updatedAt: serverTimestamp(),
            updatedBy: userId,
            ...(userName ? { updatedByName: userName } : {}),
          },
          { merge: true },
        );
      } catch (err) {
        console.warn('staffPlanning/hub write failed:', err);
        setPersistError(err instanceof Error ? err.message : 'Could not save shared planning preference');
        setSimulatedProjectIds(prev);
        idsRef.current = prev;
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [allowed, userId, userName],
  );

  const writeSchedules = useCallback(
    async (next: SimulatedSchedulesByProject) => {
      if (!allowed || !userId) {
        setPersistError('Sign in required to save planning selections');
        return;
      }
      const prev = schedulesRef.current;
      setSaving(true);
      setPersistError(null);
      setSimulatedSchedules(next);
      schedulesRef.current = next;
      try {
        await setDoc(
          doc(db, STAFF_PLANNING_COLLECTION, STAFF_PLANNING_HUB_DOC_ID),
          {
            simulatedSchedules: next,
            updatedAt: serverTimestamp(),
            updatedBy: userId,
            ...(userName ? { updatedByName: userName } : {}),
          },
          { merge: true },
        );
      } catch (err) {
        console.warn('staffPlanning/hub schedule write failed:', err);
        setPersistError(err instanceof Error ? err.message : 'Could not save simulated task dates');
        setSimulatedSchedules(prev);
        schedulesRef.current = prev;
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [allowed, userId, userName],
  );

  const saveSimulatedItemSchedule = useCallback(
    async (projectId: string, itemId: string, patch: SimulatedScheduleItem) => {
      const pid = projectId.trim();
      const id = itemId.trim();
      if (!pid || !id) return;
      const prev = schedulesRef.current;
      const projectMap = { ...(prev[pid] || {}), [id]: { ...(prev[pid]?.[id] || {}), ...patch } };
      await writeSchedules({ ...prev, [pid]: projectMap });
    },
    [writeSchedules],
  );

  const saveSimulatedProjectSchedule = useCallback(
    async (projectId: string, items: Record<string, SimulatedScheduleItem>) => {
      const pid = projectId.trim();
      if (!pid || !Object.keys(items).length) return;
      const prev = schedulesRef.current;
      const projectMap = { ...(prev[pid] || {}) };
      for (const [itemId, patch] of Object.entries(items)) {
        const id = itemId.trim();
        if (!id) continue;
        // Fill gaps only. A date just dragged into the overlay wins over a checklist copy.
        projectMap[id] = { ...patch, ...(projectMap[id] || {}) };
      }
      await writeSchedules({ ...prev, [pid]: projectMap });
    },
    [writeSchedules],
  );

  const setProjectSimulated = useCallback(
    async (projectId: string, on: boolean) => {
      const id = projectId.trim();
      if (!id) return;
      const current = idsRef.current;
      const has = current.includes(id);
      if (on && has) return;
      if (!on && !has) return;
      const next = on ? [...current, id] : current.filter((existing) => existing !== id);
      await persistIds(next);
    },
    [persistIds],
  );

  const toggleProject = useCallback(
    async (projectId: string) => {
      const id = projectId.trim();
      if (!id) return;
      await setProjectSimulated(id, !idsRef.current.includes(id));
    },
    [setProjectSimulated],
  );

  const clearAll = useCallback(async () => {
    if (!idsRef.current.length) return;
    await persistIds([]);
  }, [persistIds]);

  return {
    simulatedProjectIds: allowed ? simulatedProjectIds : [],
    simulatedSchedules: allowed ? simulatedSchedules : {},
    saveSimulatedItemSchedule,
    saveSimulatedProjectSchedule,
    hasSimulations: allowed && hasSimulations,
    hydrated: !allowed || hydrated,
    saving,
    persistError: allowed ? persistError : null,
    setProjectSimulated,
    toggleProject,
    clearAll,
    templateItems: allowed ? templateItems : null,
    templateLoadError: allowed ? templateLoadError : null,
  };
}
